import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ThemeToggle from './ThemeToggle';
import Logo from './Logo';
import './Auth.css';

const Login = ({ setIsAuthenticated }) => {
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [step, setStep] = useState(1); // 1 - email+пароль, 2 - PIN при новом IP, 3 - ожидание Telegram, 4 - привязка Telegram
  const [loginAttemptId, setLoginAttemptId] = useState(null);
  const [pinForNewIp, setPinForNewIp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingId, setPendingId] = useState(null);
  const [telegramLinkUrl, setTelegramLinkUrl] = useState('');
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(90);
  const [expiresAt, setExpiresAt] = useState(null);
  const pollingRef = useRef(null);
  const timerRef = useRef(null);
  const navigate = useNavigate();

  // Функция для применения фона темы
  const applyThemeBackground = useCallback(() => {
    const theme = document.documentElement.getAttribute('data-theme') || 'dark';
    const publicUrl = process.env.PUBLIC_URL || '';
    const darkBg = `${publicUrl}/backgrounds/background-dark.png`;
    const lightBg = `${publicUrl}/backgrounds/background-light.png`;
    
    const bgUrl = theme === 'light' ? lightBg : darkBg;
    document.body.style.backgroundImage = `url(${bgUrl})`;
    document.body.style.backgroundColor = '';
    document.body.style.backgroundSize = 'cover';
    document.body.style.backgroundPosition = 'center';
    document.body.style.backgroundRepeat = 'no-repeat';
    document.body.style.backgroundAttachment = 'fixed';
    const mobileBg = document.getElementById('mobile-bg');
    if (mobileBg) mobileBg.style.backgroundImage = `url(${bgUrl})`;
  }, []);

  // Применение фона и блокировка overscroll на мобильных
  useEffect(() => {
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.overscrollBehavior = 'none';
    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';

    applyThemeBackground();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          applyThemeBackground();
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => {
      document.documentElement.style.overflow = '';
      document.documentElement.style.overscrollBehavior = '';
      document.body.style.overflow = '';
      document.body.style.overscrollBehavior = '';
      observer.disconnect();
    };
  }, [applyThemeBackground]);

  // Cleanup polling and timer on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Таймер обратного отсчета (для шага ожидания Telegram)
  useEffect(() => {
    if (step === 3 && expiresAt) {
      const updateTimer = () => {
        const now = Date.now();
        const remaining = Math.max(0, Math.ceil((expiresAt - now) / 1000));
        setTimeRemaining(remaining);

        if (remaining === 0) {
          // Время истекло
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
          }
          if (timerRef.current) {
            clearInterval(timerRef.current);
          }
          setError('Время ожидания подтверждения истекло. Попробуйте снова.');
          setStep(1);
          setPendingId(null);
          setLoginAttemptId(null);
          setCheckingStatus(false);
          setExpiresAt(null);
        }
      };

      updateTimer();
      timerRef.current = setInterval(updateTimer, 1000);
      return () => {
        if (timerRef.current) clearInterval(timerRef.current);
      };
    }
  }, [step, expiresAt]);

  // Polling для проверки статуса подтверждения в Telegram
  useEffect(() => {
    if (step === 3 && pendingId) {
      setCheckingStatus(true);
      
      const checkStatus = async () => {
        try {
          const response = await axios.post('/api/auth/check-2fa-status', {
            pendingId
          });

          if (response.data.status === 'confirmed') {
            if (pollingRef.current) clearInterval(pollingRef.current);
            if (timerRef.current) clearInterval(timerRef.current);
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('user', JSON.stringify(response.data.user));
            if (response.data.sessionExpiresAt) {
              localStorage.setItem('sessionExpiresAt', response.data.sessionExpiresAt);
            }
            if (response.data.promptSetPinAfterLogin) {
              sessionStorage.setItem('promptSetPinAfterLogin', '1');
            }
            setIsAuthenticated(true);
            navigate('/discordmoderation/overview');
          } else if (response.data.status === 'rejected') {
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
            }
            if (timerRef.current) {
              clearInterval(timerRef.current);
            }
            setError('Вход был отклонён в Telegram');
            setStep(1);
            setPendingId(null);
            setLoginAttemptId(null);
            setCheckingStatus(false);
            setExpiresAt(null);
          } else if (response.data.status === 'expired') {
            if (pollingRef.current) clearInterval(pollingRef.current);
            if (timerRef.current) clearInterval(timerRef.current);
            setError('Время ожидания подтверждения истекло. Попробуйте снова.');
            setStep(1);
            setPendingId(null);
            setLoginAttemptId(null);
            setCheckingStatus(false);
            setExpiresAt(null);
          } else if (response.data.status === 'not_found') {
            if (pollingRef.current) clearInterval(pollingRef.current);
            if (timerRef.current) clearInterval(timerRef.current);
            setError('Запрос не найден. Попробуйте снова.');
            setStep(1);
            setPendingId(null);
            setLoginAttemptId(null);
            setCheckingStatus(false);
            setExpiresAt(null);
          }
        } catch (err) {
          console.error('Error checking 2FA status:', err);
        }
      };

      // Проверяем сразу
      checkStatus();
      
      // Затем каждые 2 секунды
      pollingRef.current = setInterval(checkStatus, 2000);

      return () => {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
        }
      };
    }
  }, [step, pendingId, navigate, setIsAuthenticated]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await axios.post('/api/auth/login', formData);

      if (response.data.requiresPinAndTelegram && response.data.loginAttemptId) {
        setLoginAttemptId(response.data.loginAttemptId);
        setStep(2);
        setLoading(false);
        return;
      }

      if (response.data.requires2FA) {
        if (response.data.telegramNotLinked) {
          setStep(4);
          setTelegramLinkUrl(response.data.telegramLinkUrl);
        } else {
          setStep(3);
          setPendingId(response.data.pendingId);
          setExpiresAt(Date.now() + 90 * 1000);
          setTimeRemaining(90);
        }
        setLoading(false);
        return;
      }

      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        if (response.data.sessionExpiresAt) {
          localStorage.setItem('sessionExpiresAt', response.data.sessionExpiresAt);
        }
        setIsAuthenticated(true);
        navigate('/discordmoderation/overview');
      } else {
        setError(response.data.error || 'Ошибка входа');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка при входе');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPinForNewIp = async (e) => {
    e.preventDefault();
    const pinStr = pinForNewIp.replace(/\D/g, '');
    if (pinStr.length < 4 || pinStr.length > 8 || !loginAttemptId) {
      setError('Введите PIN (4–8 цифр)');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await axios.post('/api/auth/verify-pin-for-login', { loginAttemptId, pin: pinStr });
      setPendingId(res.data.pendingId);
      setStep(3);
      setExpiresAt(Date.now() + 90 * 1000);
      setTimeRemaining(90);
      setPinForNewIp('');
    } catch (err) {
      setError(err.response?.data?.error || 'Неверный PIN или время истекло. Войдите снова.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    setStep(1);
    setPendingId(null);
    setLoginAttemptId(null);
    setPinForNewIp('');
    setTelegramLinkUrl('');
    setError('');
    setCheckingStatus(false);
    setVerificationCode('');
    setVerifyingCode(false);
    setTimeRemaining(90);
    setExpiresAt(null);
  };

  const handleRetryAfterLink = () => {
    setStep(1);
    setTelegramLinkUrl('');
    setError('');
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!verificationCode || verificationCode.trim().length !== 6) {
      setError('Введите 6-значный код');
      return;
    }

    setVerifyingCode(true);
    setError('');

    try {
      const response = await axios.post('/api/auth/verify-2fa-code', {
        pendingId,
        code: verificationCode.trim()
      });

      if (response.data.status === 'confirmed') {
        if (pollingRef.current) clearInterval(pollingRef.current);
        if (timerRef.current) clearInterval(timerRef.current);
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        if (response.data.sessionExpiresAt) {
          localStorage.setItem('sessionExpiresAt', response.data.sessionExpiresAt);
        }
        if (response.data.promptSetPinAfterLogin) {
          sessionStorage.setItem('promptSetPinAfterLogin', '1');
        }
        setIsAuthenticated(true);
        navigate('/discordmoderation/overview');
      } else {
        setError(response.data.error || 'Неверный код');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Ошибка при проверке кода';
      setError(errorMessage);
    } finally {
      setVerifyingCode(false);
    }
  };

  return (
    <div className="auth-container">
      <ThemeToggle />
      <div className="auth-box">
        <div className="auth-left-panel">
          <div className="auth-header">
            <Logo variant="auth" style={{ marginBottom: '12px' }} />
            <p>
              {step === 1 && 'Войдите с помощью email и пароля'}
              {step === 2 && 'Вход с нового IP: введите PIN-код'}
              {step === 3 && (
                <>
                  Подтвердите вход в{' '}
                  <svg viewBox="0 0 24 24" fill="#ff8000" width="16" height="16" style={{ display: 'inline-block', verticalAlign: 'middle', margin: '0 4px' }}>
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.912.489-1.302.481-.428-.008-1.252-.241-1.865-.44-.752-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635.099-.002.321.023.465.141.121.099.154.232.17.325.015.094.034.31.019.478z"/>
                  </svg>
                  Telegram
                </>
              )}
              {step === 4 && 'Привяжите Telegram для входа'}
            </p>
          </div>

          {error && (
            <div className="auth-error-message">
              <span className="auth-error-icon">⚠</span>
              <span className="auth-error-text">{error}</span>
            </div>
          )}

          <div className="auth-step-container">
            {step === 1 && (
              <form onSubmit={handleSubmit} className="auth-form">
                <div className="form-group">
                  <label htmlFor="username">EMAIL</label>
                  <input
                    type="email"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    required
                    placeholder="Email"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="password">ПАРОЛЬ</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="Пароль"
                  />
                </div>
                <button type="submit" className="auth-button" disabled={loading}>
                  {loading ? 'Проверка...' : 'Войти'}
                </button>
              </form>
            )}

            {step === 2 && (
              <div className="auth-form pin-step-box">
                <p className="pin-step-hint">Обнаружен вход с нового IP. Введите PIN-код, затем подтвердите вход в Telegram.</p>
                <form onSubmit={handleVerifyPinForNewIp} className="auth-form">
                  <div className="form-group">
                    <label htmlFor="pin-new-ip">PIN-КОД (4–8 цифр)</label>
                    <input
                      type="password"
                      id="pin-new-ip"
                      inputMode="numeric"
                      autoComplete="off"
                      value={pinForNewIp}
                      onChange={(e) => {
                        setPinForNewIp(e.target.value.replace(/\D/g, '').slice(0, 8));
                        setError('');
                      }}
                      placeholder="••••"
                      maxLength={8}
                      className="pin-input-auth"
                    />
                  </div>
                  <button
                    type="submit"
                    className="auth-button"
                    disabled={loading || pinForNewIp.replace(/\D/g, '').length < 4}
                  >
                    {loading ? 'Проверка...' : 'Продолжить'}
                  </button>
                </form>
                <button type="button" className="auth-button auth-button-secondary" onClick={handleBackToLogin}>
                  Отмена
                </button>
              </div>
            )}

            {step === 3 && (
              <div className="auth-form">
                <div className="telegram-waiting">
                  <p style={{ marginBottom: '4px', fontSize: '13px' }}>Запрос отправлен в ваш Telegram! Необходимо нажать на кнопку "Подтвердить" чтобы войти.</p>
                  <div className="verification-divider" style={{ marginTop: '8px', marginBottom: '0' }}>
                    <span>или</span>
                  </div>
                </div>

                <form onSubmit={handleVerifyCode} className="verification-code-form">
                  <div className="form-group">
                    <label htmlFor="verificationCode">ВВЕДИТЕ ПРОВЕРОЧНЫЙ КОД</label>
                    <input
                      type="text"
                      id="verificationCode"
                      name="verificationCode"
                      value={verificationCode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setVerificationCode(value);
                        setError('');
                      }}
                      placeholder="000000"
                      maxLength="6"
                      pattern="[0-9]{6}"
                      className="code-input"
                      autoComplete="one-time-code"
                      inputMode="numeric"
                    />
                  </div>

                  <button 
                    type="submit" 
                    className="auth-button" 
                    disabled={verifyingCode || verificationCode.length !== 6}
                  >
                    {verifyingCode ? 'Проверка...' : 'Подтвердить'}
                  </button>
                </form>

                <div className="verification-timer">
                  <span className="timer-text">Осталось:</span>
                  <span className={`timer-value ${timeRemaining <= 10 ? 'timer-warning' : ''}`}>
                    {timeRemaining}
                  </span>
                  <span className="timer-unit">сек</span>
                </div>

                <button 
                  type="button" 
                  className="auth-button auth-button-secondary" 
                  onClick={handleBackToLogin}
                >
                  Отмена
                </button>
              </div>
            )}

            {step === 4 && (
              <div className="auth-form">
                <div className="telegram-link-section">
                <div className="telegram-link-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.912.489-1.302.481-.428-.008-1.252-.241-1.865-.44-.752-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635.099-.002.321.023.465.141.121.099.154.232.17.325.015.094.034.31.019.478z"/>
                  </svg>
                </div>
                <h3>Привяжите Telegram</h3>
                <p>Для двухфакторной авторизации необходимо привязать Telegram к вашему аккаунту.</p>
                
                <a 
                  href={telegramLinkUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="auth-button telegram-link-button"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" style={{ marginRight: '10px' }}>
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.912.489-1.302.481-.428-.008-1.252-.241-1.865-.44-.752-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635.099-.002.321.023.465.141.121.099.154.232.17.325.015.094.034.31.019.478z"/>
                  </svg>
                  Привязать Telegram
                </a>

                <p className="telegram-link-hint">После привязки вернитесь и нажмите "Войти"</p>
              </div>

              <button 
                type="button" 
                className="auth-button" 
                onClick={handleRetryAfterLink}
              >
                Войти
              </button>

              <button 
                type="button" 
                className="auth-button auth-button-secondary" 
                onClick={handleBackToLogin}
              >
                Назад
              </button>
              </div>
            )}
          </div>

        </div>

      </div>
    </div>
  );
};

export default Login;
